/**
 * ToastHost — renders the active toast stack.
 * Mount once in MainLayout. Listens to the module-level toast store.
 */
import React, { useState, useEffect } from 'react';
import { subscribeToasts, dismissToast, Toast } from '../../utils/toast';

const TYPE_COLORS: Record<Toast['type'], { bg: string; border: string; text: string }> = {
  error:   { bg: '#fee2e2', border: '#fecaca', text: '#dc2626' },
  success: { bg: '#dcfce7', border: '#bbf7d0', text: '#166534' },
  info:    { bg: '#e0f2fe', border: '#bae6fd', text: '#0369a1' },
  warning: { bg: '#fef3c7', border: '#fde68a', text: '#92400e' },
};

const ToastHost: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    return subscribeToasts(setToasts);
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxWidth: '420px',
        width: 'calc(100vw - 48px)',
        pointerEvents: 'none',
      }}
    >
      {toasts.map(toast => {
        const colors = TYPE_COLORS[toast.type];
        return (
          <div
            key={toast.id}
            role="alert"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              padding: '12px 14px',
              backgroundColor: colors.bg,
              border: `1px solid ${colors.border}`,
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              color: colors.text,
              fontSize: '14px',
              lineHeight: '1.4',
              gap: '10px',
              pointerEvents: 'all',
            }}
          >
            <span style={{ flex: 1, wordBreak: 'break-word' }}>{toast.message}</span>
            <button
              onClick={() => dismissToast(toast.id)}
              aria-label="Dismiss"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: colors.text,
                fontSize: '14px',
                lineHeight: 1,
                flexShrink: 0,
                padding: '0 2px',
                opacity: 0.7,
              }}
            >
              x
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ToastHost;
